import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import toPairs from 'lodash-es/toPairs'

import { EMPTY_OBJECT } from '../../../../constants/common'
import { parseServerValidation } from '../../../../utils/validation'
import {
  EMAIL_CREATION_FORM_FIELD_NAME_FROM,
  EMAIL_CREATION_FORM_FIELD_NAME_HAS_NO_EMAIL_ACCOUNTS,
} from '../../../Email'
import { useFormValidationErrorsNotification } from '../../../Form'
import { DocFlowSendingWidgetProcessFieldName } from '../../DocFlowSending'
import {
  DOC_FLOW_SENDING_EMAIL_SETTINGS_NOTIFICATION_ID_PERMANENT,
  DOC_FLOW_SENDING_EMAIL_SETTINGS_NOTIFICATION_ID_UNAVAILABLE,
  getDocFlowSendingEmailSettingsPayload,
  getDocFlowSendingEmailSettingsPermanentNotifications,
  getDocFlowSendingEmailSettingsUnavailableNotification,
  getDocFlowSendingEmailSettingsWidgetNotifications,
  parseDocFlowEmailSettingsServerError,
} from '../../DocFlowWidgets'
import { useDocFlowContext } from '../useDocFlowContext'
import { useDocFlowEmailBlackList } from '../useDocFlowEmailBlackList'
import { useDocFlowSendingContext } from '../useDocFlowSendingContext'
import { useDocFlowSendingWidgetNotifications } from '../useDocFlowSendingWidgetNotifications'

export function useDocFlowEmailSettings({ index }) {
  const { t } = useTranslation()

  const { phaseDisabled } = useDocFlowContext()

  const {
    emailAIDraftRef,
    emailFormikRef,
    handleClickNextWidget,
    handleClickPreviousWidget,
    processData,
    usersTableData: { handleReload },
  } = useDocFlowSendingContext()

  const [errors, setErrors] = useState(null)

  const isEmailDisabledForAll = useMemo(
    () =>
      processData?.[DocFlowSendingWidgetProcessFieldName.IsEmailDisabledForAll],
    [processData]
  )

  const widgetNotifications = useMemo(
    () => getDocFlowSendingEmailSettingsWidgetNotifications(t),
    []
  )

  const {
    handleAddNotification,
    handlePrependNotification,
    handleRemoveNotification,
    notifications,
  } = useDocFlowSendingWidgetNotifications({
    phase: DocFlowSendingWidgetProcessFieldName.EmailSettingsPhase,
    widgetNotifications,
  })

  useFormValidationErrorsNotification({
    // emailFormikRef is set up by parent in DocFlowSendingContext as a
    // ref-forwarded handle to the email step's Formik instance.
    // useFormValidationErrorsNotification reads errors/submitCount off
    // the context on every render — switching to a stateful contract
    // would require touching every sending-widget hook that shares this
    // ref. Tracked separately.
    // eslint-disable-next-line react-hooks/refs
    formikContext: emailFormikRef.current || EMPTY_OBJECT,
    onRemoveNotification: handleRemoveNotification,
    onSetNotification: handlePrependNotification,
  })

  const { checkDocFlowEmailBlackList } = useDocFlowEmailBlackList({
    widgetNotifications,
    onPrependNotification: handlePrependNotification,
    onRemoveNotification: handleRemoveNotification,
  })

  useEffect(() => {
    if (isEmailDisabledForAll) {
      handleRemoveNotification(
        DOC_FLOW_SENDING_EMAIL_SETTINGS_NOTIFICATION_ID_PERMANENT
      )

      handleAddNotification(
        getDocFlowSendingEmailSettingsUnavailableNotification(t)
      )
    } else {
      handleRemoveNotification(
        DOC_FLOW_SENDING_EMAIL_SETTINGS_NOTIFICATION_ID_UNAVAILABLE
      )

      handleAddNotification(
        getDocFlowSendingEmailSettingsPermanentNotifications(t)
      )
    }
  }, [isEmailDisabledForAll])

  const handleSetServerError = useCallback(
    async error => {
      setErrors(
        parseServerValidation(parseDocFlowEmailSettingsServerError(error, t))
      )

      await emailFormikRef.current.setFieldValue(
        EMAIL_CREATION_FORM_FIELD_NAME_FROM,
        null
      )

      handleRemoveNotification(widgetNotifications.success.id)
    },
    [widgetNotifications]
  )

  const handleClickNext = useCallback(async () => {
    if (phaseDisabled) {
      return handleClickNextWidget(index)
    }

    if (emailAIDraftRef.current) {
      handlePrependNotification(widgetNotifications.aiDraftNotApplied)

      return
    } else {
      handleRemoveNotification(widgetNotifications.aiDraftNotApplied.id)
    }

    const {
      setSubmitting,
      validateForm,
      values: emailFormValues,
    } = emailFormikRef.current || EMPTY_OBJECT

    if (emailFormikRef.current) {
      setSubmitting(true)

      const errors = await validateForm()
      const values = await emailFormValues

      const recipientsWithoutEmail =
        values[EMAIL_CREATION_FORM_FIELD_NAME_HAS_NO_EMAIL_ACCOUNTS]

      if (recipientsWithoutEmail) {
        handlePrependNotification(widgetNotifications.recipientsWithoutEmail)
      } else {
        handleRemoveNotification(widgetNotifications.recipientsWithoutEmail.id)
      }

      const recipientsInBlackList = await checkDocFlowEmailBlackList(values)

      const hasErrors = toPairs(errors).some(([, isError]) => isError)

      if (hasErrors) {
        handlePrependNotification(widgetNotifications.requiredFields)
      } else {
        handleRemoveNotification(widgetNotifications.requiredFields.id)
      }

      if (hasErrors || recipientsInBlackList || recipientsWithoutEmail) {
        setSubmitting(false)

        handleRemoveNotification(widgetNotifications.success.id)

        return
      }
    }

    handleClickNextWidget(
      getDocFlowSendingEmailSettingsPayload(emailFormikRef.current)
    )
      .then(() => {
        setSubmitting?.(false)

        setErrors(null)

        // TODO move to another widget,  which will be before result widget
        handleReload()
      })
      .catch(handleSetServerError)
  }, [handleClickNextWidget, handleSetServerError, index, widgetNotifications])

  const handleClickPrevious = useCallback(() => {
    if (phaseDisabled) {
      return handleClickPreviousWidget(index)
    }

    handleClickPreviousWidget(
      getDocFlowSendingEmailSettingsPayload(emailFormikRef.current)
    )
      .then(() => {
        handleReload()

        handleRemoveNotification(widgetNotifications.error.id)

        handleRemoveNotification(widgetNotifications.recipientsInBlackList.id)
      })
      .catch(handleSetServerError)
  }, [handleClickPreviousWidget, handleSetServerError])

  return {
    handleClickNext,
    handleClickPrevious,
    isError: !!errors,
    notifications,
  }
}
